import { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  InputLabel,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchTeachersAsync,
  selectAllTeachers,
  selectTeachersLoading,
  selectTeachersError,
} from "../../../Slices/teachersSlice";
import {
  fetchGradesAsync,
  selectAllGrades,
  selectGradesLoading,
  selectGradesError,
} from "../../../Slices/gradesSlice";
import { isAuthenticated, addClass } from "../../../ApiService/ApiService";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import CustomDialog from "../../../Components/CustomDialog";

function AddClass({ fetchClasses, setShowAddClass }) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchTeachersAsync());
    dispatch(fetchGradesAsync());
  }, [dispatch]);

  const teachers = useSelector(selectAllTeachers);
  const teacherLoading = useSelector(selectTeachersLoading);
  const teacherError = useSelector(selectTeachersError);

  const grades = useSelector(selectAllGrades);
  const gradesLoading = useSelector(selectGradesLoading);
  const gradesError = useSelector(selectGradesError);

  const schoolId = parseInt(sessionStorage.getItem("schoolId"));

  const [formData, setFormData] = useState({
    gradeId: "",
    section: "",
    teacherId: "",
    schoolId: schoolId,
  });
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.gradeId || !formData.teacherId || !formData.schoolId) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please fill all the details",
        buttons: [{ name: "OK", onPress: handleDialogClose }],
      });
      return;
    }
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const response = await addClass(formData);
        // console.log("RES", response);
        if (response) {
          if (response?.data?.status === 200) {
            setDialog({
              visible: true,
              title: "Success",
              message: "Class added",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
            setFormData({
              gradeId: "",
              section: "",
              teacherId: "",
              schoolId: schoolId,
            });
            fetchClasses();
            setShowAddClass(false);
          } else {
            const errorMessage =
              response?.data?.errorMessage || "Something went wrong";
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      console.log("Failed to add class", error);
      setDialog({
        visible: true,
        title: "Error",
        message: "Failed to add class",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box sx={{ maxWidth: "75%", margin: "auto", padding: 3 }}>
        <Paper elevation={3} sx={{ padding: 5 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: 22, color: "black" }}
          >
            Add Class
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="grade" size="small">
                    Grade *
                  </InputLabel>
                  <Select
                    id="gradeId"
                    name="gradeId"
                    labelId="grade"
                    value={formData.gradeId}
                    onChange={handleChange}
                    label="Grade"
                    size="small"
                    required
                    disabled={gradesError || gradesLoading}
                  >
                    {gradesLoading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : gradesError ? (
                      <MenuItem disabled>Error: Failed to load grades</MenuItem>
                    ) : (
                      grades?.map((grade) => (
                        <MenuItem key={grade?.id} value={grade?.id}>
                          {grade?.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  fullWidth
                  label="Section"
                  name="section"
                  value={formData.section}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth>
                  <InputLabel id="teacher" size="small">
                    Class Teacher *
                  </InputLabel>
                  <Select
                    id="teacherId"
                    name="teacherId"
                    labelId="teacher"
                    value={formData.teacherId}
                    onChange={handleChange}
                    label="Class Teacher"
                    size="small"
                    required
                  >
                    {teacherLoading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : teacherError ? (
                      <MenuItem disabled>
                        Error: Failed to load teachers
                      </MenuItem>
                    ) : (
                      teachers?.data?.map((teacher) => (
                        <MenuItem key={teacher?.id} value={teacher?.id}>
                          {`${teacher?.firstName} ${
                            teacher?.middleName ? teacher?.middleName + " " : ""
                          }${teacher?.lastName}`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => setShowAddClass(false)}
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: "unset" }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={<ArrowForward />}
                  sx={{ textTransform: "unset" }}
                >
                  {loading ? "Loading..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default AddClass;
